import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Briefcase,
  MessageCircle,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  Calendar,
  User,
  X,
  Check,
  Filter,
  RefreshCw,
  Loader2,
  ExternalLink
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/Button";
import SharedFooter3 from "../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  fetchUserNotificationsByUser,
  updateUserNotification,
  deleteUserNotification,
  bulkUpdateUserNotifications,
  clearUserSpecificNotificationsError,
  decrementUnreadCount
} from "../redux/Slices/UserNotificationSlice";
import { UserNotification, NotificationTypeEnum, NotificationPriorityEnum, NotificationStatusEnum, RoleEnum } from "../modules/shared/model";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedHeader4 from "../Headers/SharedHeader4";

const NotificationsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const {
    userSpecificNotifications,
    userSpecificNotificationsLoading,
    userSpecificNotificationsError,
    userSpecificNotificationsHasNextPage,
    userSpecificNotificationsCurrentPage,
    userSpecificNotificationsTotalCount
  } = useAppSelector((state) => state.userNotifications);

  const [selectedFilter, setSelectedFilter] = useState<string | NotificationTypeEnum>("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Sync local currentPage with Redux state when it changes
  useEffect(() => {
    if (userSpecificNotificationsCurrentPage && userSpecificNotificationsCurrentPage !== currentPage) {
      setCurrentPage(userSpecificNotificationsCurrentPage);
    }
  }, [userSpecificNotificationsCurrentPage]);

  // Fetch notifications on component mount
  useEffect(() => {
    if (user?.id) {
      const queryParams: Record<string, string | number | boolean> = {
        page: currentPage,
        limit: 10,
        sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
      };
      
      // Build filters object
      const filters: Record<string, string> = {};
      
      // Add status filter if showing unread only
      if (showUnreadOnly) {
        filters.status = NotificationStatusEnum.Unread.toString();
      }
      
      // Add type filter if not showing all
      if (selectedFilter !== "all") {
        filters.type = selectedFilter.toString();
      }
      
      // Add filters to query params if any filters exist
      if (Object.keys(filters).length > 0) {
        queryParams.filters = JSON.stringify(filters);
      }
      
      dispatch(fetchUserNotificationsByUser({
        userId: user.id,
        query: queryParams
      }));
    }
  }, [dispatch, user?.id, currentPage, showUnreadOnly, selectedFilter]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearUserSpecificNotificationsError());
    };
  }, [dispatch]);

  // Filter options based on notification types
  const filters = [
    { value: "all", label: t("notifications.allNotifications") },
    { value: NotificationTypeEnum["In-App"], label: t("notifications.inApp") },
    { value: NotificationTypeEnum.Banner, label: t("notifications.banner") },
    { value: NotificationTypeEnum.Popup, label: t("notifications.popup") },
    { value: NotificationTypeEnum.Email, label: t("notifications.emailType") },
    { value: NotificationTypeEnum.Announcement, label: t("notifications.announcements") },
    { value: NotificationTypeEnum.Marketing, label: t("notifications.marketing") }
  ];

  // Get icon based on notification type
  const getIcon = (type: NotificationTypeEnum) => {
    switch (type) {
      case NotificationTypeEnum["In-App"]:
        return <Bell className="h-5 w-5 text-blue-600" />;
      case NotificationTypeEnum.Banner:
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case NotificationTypeEnum.Popup:
        return <MessageCircle className="h-5 w-5 text-green-600" />;
      case NotificationTypeEnum.Email:
        return <DollarSign className="h-5 w-5 text-purple-600" />;
      case NotificationTypeEnum.Announcement:
        return <Bell className="h-5 w-5 text-indigo-600" />;
      case NotificationTypeEnum.Marketing:
        return <Bell className="h-5 w-5 text-pink-600" />;
      default:
        return <Bell className="h-5 w-5 text-gray-600" />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority: NotificationPriorityEnum) => {
    switch (priority) {
      case NotificationPriorityEnum.High:
        return "text-red-600";
      case NotificationPriorityEnum.Medium:
        return "text-yellow-600";
      case NotificationPriorityEnum.Low:
        return "text-green-600";
      case NotificationPriorityEnum.Normal:
        return "text-blue-600";
      default:
        return "text-gray-600";
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  // Filter notifications
  const filteredNotifications = userSpecificNotifications.filter(notification => {
    if (showUnreadOnly && Number(notification.status?.id) === NotificationStatusEnum.Read) return false;
    if (selectedFilter === "all") return true;
    return Number(notification.type?.id) === selectedFilter;
  });

  // Mark notification as read
  const markAsRead = async (id: string) => {
    try {
      await dispatch(updateUserNotification({
        id,
        data: {
          status: { id: NotificationStatusEnum.Read }
        }
      }));
      
      // Decrement unread count
      dispatch(decrementUnreadCount(1));
      
      // Refresh notifications after update
      if (user?.id) {
        const queryParams: Record<string, string | number | boolean> = {
          page: 1,
          limit: 10,
          sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
        };
        
        // Build filters object
        const filters: Record<string, string> = {};
        
        if (showUnreadOnly) {
          filters.status = NotificationStatusEnum.Unread.toString();
        }
        
        if (selectedFilter !== "all") {
          filters.type = selectedFilter.toString();
        }
        
        // Add filters to query params if any filters exist
        if (Object.keys(filters).length > 0) {
          queryParams.filters = JSON.stringify(filters);
        }
        
        dispatch(fetchUserNotificationsByUser({
          userId: user.id,
          query: queryParams
        }));
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = userSpecificNotifications.filter(
        notification => Number(notification.status?.id) === NotificationStatusEnum.Unread
      );

      if (unreadNotifications.length > 0) {
        const bulkUpdates = unreadNotifications.map(notification => ({
          id: notification.id,
          data: {
            status: { id: NotificationStatusEnum.Read }
          }
        }));

        await dispatch(bulkUpdateUserNotifications(bulkUpdates));
        
        // Decrement unread count by the number of notifications marked as read
        dispatch(decrementUnreadCount(unreadNotifications.length));
        
        // Refresh notifications after bulk update
        if (user?.id) {
          const queryParams: Record<string, string | number | boolean> = {
            page: 1,
            limit: 10,
            sort: JSON.stringify([{ orderBy: "createdAt", order: "DESC" }])
          };
          
          // Build filters object
          const filters: Record<string, string> = {};
          
          if (showUnreadOnly) {
            filters.status = NotificationStatusEnum.Unread.toString();
          }
          
          if (selectedFilter !== "all") {
            filters.type = selectedFilter.toString();
          }
          
          // Add filters to query params if any filters exist
          if (Object.keys(filters).length > 0) {
            queryParams.filters = JSON.stringify(filters);
          }
          
          dispatch(fetchUserNotificationsByUser({
            userId: user.id,
            query: queryParams
          }));
          setCurrentPage(1);
        }
      }
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  // Delete notification
  const deleteNotification = async (id: string) => {
    try {
      await dispatch(deleteUserNotification(id));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  // Load more notifications
  const loadMoreNotifications = () => {
    if (userSpecificNotificationsHasNextPage && !userSpecificNotificationsLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
    }
  };

  // Go to specific page
  const goToPage = (page: number) => {
    if (page >= 1 && !userSpecificNotificationsLoading) {
      setCurrentPage(page);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil((userSpecificNotificationsTotalCount || 0) / 10);

  // Handle notification URL navigation
  const handleNotificationClick = (notification: UserNotification) => {
    // Mark as read if unread
    if (Number(notification.status?.id) === NotificationStatusEnum.Unread) {
      markAsRead(notification.id);
    }
    
    // Parse type.id as number (it comes as string from backend)
    const notificationTypeId = Number(notification.type?.id);
    
    // If notification is Announcement type (5), show announcement dialog
    if (notificationTypeId === NotificationTypeEnum.Announcement) {
      // Parse announcement content from message field
      let announcementTitle = notification.title || t("notifications.newAnnouncement");
      let announcementContent = notification.message || "";
      
      try {
        // Try to parse JSON if message contains announcement data
        const parsedMessage = JSON.parse(notification.message || "{}");
        if (parsedMessage.announcementTitle) {
          announcementTitle = parsedMessage.announcementTitle;
        }
        if (parsedMessage.announcementContent) {
          announcementContent = parsedMessage.announcementContent;
        }
      } catch (e) {
        // If not JSON, use message as is
        announcementContent = notification.message || "";
      }
      
      // Trigger custom event to show announcement popup
      const event = new CustomEvent("showNotificationPopup", {
        detail: {
          id: notification.id,
          notificationId: notification.id,
          title: notification.title || t("notifications.newAnnouncement"),
          message: notification.message,  // Pass full JSON message
          priority: notification.priority,
          type: notification.type,
          url: notification.url,
          expiryTime: notification.expiryTime,
          status: notification.status,
          createdAt: notification.createdAt,
        }
      });
      
      window.dispatchEvent(event);
      return;
    }
    // If notification is Marketing type (6), show marketing popup dialog
    else if (notificationTypeId === NotificationTypeEnum.Marketing) {
      // Parse marketing popup data from message field
      let popupData: any = {};
      
      try {
        // Parse JSON message containing popup data
        popupData = JSON.parse(notification.message || "{}");
      } catch (e) {
        console.error("Failed to parse marketing popup data:", e);
      }
      
      // Trigger custom event to show marketing popup
      const event = new CustomEvent("showNotificationPopup", {
        detail: {
          id: notification.id,
          notificationId: notification.id,
          title: notification.title || t("notifications.marketingPopup"),
          message: notification.message || "",
          priority: notification.priority,
          type: notification.type,
          url: notification.url,
          expiryTime: notification.expiryTime,
          status: notification.status,
          createdAt: notification.createdAt,
          popupData: popupData,
        }
      });
      window.dispatchEvent(event);
    }
    // If notification is Popup type (2), show popup dialog
    else if (notificationTypeId === NotificationTypeEnum.Popup) {
      // Trigger custom event to show popup
      const event = new CustomEvent("showNotificationPopup", {
        detail: {
          id: notification.id,
          notificationId: notification.id,
          title: notification.title || t("notifications.notification"),
          message: notification.message || "",
          priority: notification.priority,
          type: notification.type,
          url: notification.url,
          expiryTime: notification.expiryTime,
          status: notification.status,
          createdAt: notification.createdAt
        }
      });
      window.dispatchEvent(event);
    } else if (notification.url) {
      // For other types, navigate to URL if available
      navigate(notification.url);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      {Number(user?.role.id) === RoleEnum.client ? <SharedHeader5 /> : <SharedHeader4 />}

      {/* Main content area with proper spacing */}
      <main className="flex-1 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          {/* Header Section - Enhanced Responsive Design */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 sm:mb-8">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {t("notifications.title")}
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                {t("notifications.subtitle")}
              </p>
              {userSpecificNotificationsError && (
                <p className="text-red-600 text-sm mt-2 p-2 bg-red-50 rounded-md">
                  {userSpecificNotificationsError}
                </p>
              )}
            </div>

            <div className="flex gap-2 w-full lg:w-auto">
              <Button
                variant="outline"
                onClick={markAllAsRead}
                disabled={userSpecificNotificationsLoading}
                className="flex items-center justify-center gap-2 flex-1 lg:flex-initial px-4 py-2"
              >
                {userSpecificNotificationsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">{t("notifications.markAllRead")}</span>
                <span className="sm:hidden">{t("notifications.readAll")}</span>
              </Button>
            </div>
          </div>

          {/* Filters Section - Enhanced Responsive Design */}
          <Card className="p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6">
              <div className="flex items-center gap-2 w-full lg:w-auto">
                <Filter className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700">{t("notifications.filterBy")}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 w-full lg:w-auto">
                {filters.map((filter) => (
                  <Button
                    key={filter.value}
                    variant={selectedFilter === filter.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                    setSelectedFilter(filter.value);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                    className="justify-center text-xs sm:text-sm"
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>

              <div className="flex items-center gap-2 w-full lg:w-auto lg:ml-auto">
                <input
                  type="checkbox"
                  id="unreadOnly"
                  checked={showUnreadOnly}
                  onChange={(e) => {
                    setShowUnreadOnly(e.target.checked);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                <label htmlFor="unreadOnly" className="text-sm text-gray-600 cursor-pointer">
                  {t("notifications.showUnreadOnly")}
                </label>
              </div>
            </div>
          </Card>

          {/* Notifications List - Enhanced Responsive Design */}
          <div className="space-y-3 sm:space-y-4">
            {userSpecificNotificationsLoading && userSpecificNotifications.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <Loader2 className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t("notifications.loadingNotifications")}</h3>
                <p className="text-gray-600">{t("notifications.pleaseWait")}</p>
              </div>
            ) : filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification) => (
                <Card
                  key={notification.id}
                  className={`p-4 sm:p-6 hover:shadow-md transition-all duration-200 cursor-pointer ${Number(notification.status?.id) === NotificationStatusEnum.Unread
                      ? "bg-blue-50 border-blue-200"
                      : "bg-white border-gray-200"
                    }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getIcon(Number(notification.type?.id) || NotificationTypeEnum["In-App"])}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row items-start justify-between gap-3 lg:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-medium text-gray-900 text-sm sm:text-base leading-tight">
                              {(() => {
                                // Check for translation key first
                                if (notification.titleKey) {
                                  return t(notification.titleKey, notification.messageParams || {});
                                }
                                
                                // Parse announcement title for announcement notifications
                                const notificationTypeId = Number(notification.type?.id);
                                if (notificationTypeId === NotificationTypeEnum.Announcement) {
                                  try {
                                    const parsed = JSON.parse(notification.message || "{}");
                                    return parsed.announcementTitle || notification.title || t("notifications.newAnnouncement");
                                  } catch {
                                    return notification.title || t("notifications.newAnnouncement");
                                  }
                                }
                                // Parse marketing popup title for marketing notifications
                                if (notificationTypeId === NotificationTypeEnum.Marketing) {
                                  try {
                                    const parsed = JSON.parse(notification.message || "{}");
                                    return parsed.contentTitle || notification.title || t("notifications.marketingPopup");
                                  } catch {
                                    return notification.title || t("notifications.marketingPopup");
                                  }
                                }
                                return notification.title || "";
                              })()}
                            </h3>
                            {notification.url && (
                              <ExternalLink className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            )}
                          </div>
                          {(() => {
                            // Check for translation key first
                            if (notification.messageKey) {
                              return (
                                <p className="text-gray-600 mt-1 text-sm sm:text-base leading-relaxed line-clamp-2">
                                  {String(t(notification.messageKey, notification.messageParams || {}))}
                                </p>
                              );
                            }
                            
                            // Parse type.id as number
                            const notificationTypeId = Number(notification.type?.id);
                            
                            // ANNOUNCEMENT TYPE - Hide content, show placeholder
                            if (notificationTypeId === NotificationTypeEnum.Announcement) {
                              return (
                                <p className="text-gray-600 mt-1 text-sm sm:text-base leading-relaxed italic">
                                  {t("notifications.clickToViewAnnouncementDetails")}
                                </p>
                              );
                            }
                            
                            // MARKETING TYPE - Hide content, show placeholder
                            if (notificationTypeId === NotificationTypeEnum.Marketing) {
                              return (
                                <p className="text-gray-600 mt-1 text-sm sm:text-base leading-relaxed italic">
                                  {t("notifications.clickToViewMarketingDetails")}
                                </p>
                              );
                            }
                            
                            // OTHER TYPES - Show message as-is
                            return (
                              <p className="text-gray-600 mt-1 text-sm sm:text-base leading-relaxed line-clamp-2">
                                {notification.message || ""}
                              </p>
                            );
                          })()}
                          {notification.priority && (
                            <div className="mt-2">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(Number(notification.priority.id))} bg-opacity-10`}>
                                {NotificationPriorityEnum[Number(notification.priority.id)]}
                              </span>
                            </div>
                          )}
                          {notification.url && (
                            <div className="mt-2">
                              <span className="inline-flex items-center text-xs text-blue-600 hover:text-blue-700">
                                {t("notifications.clickToViewDetails")}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 lg:flex-shrink-0">
                          <Badge
                            variant={Number(notification.status?.id) === NotificationStatusEnum.Read ? "outline" : "default"}
                            className={`text-xs ${Number(notification.status?.id) === NotificationStatusEnum.Read
                                ? "text-gray-600 border-gray-300"
                                : "bg-blue-100 text-blue-800 border-blue-200"
                              }`}
                          >
                            {Number(notification.status?.id) === NotificationStatusEnum.Read ? t("notifications.read") : t("notifications.new")}
                          </Badge>
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                            title={t("notifications.deleteNotification")}
                          >
                            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                          <Clock className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                          <span>{formatTimestamp(notification.createdAt)}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          {Number(notification.status?.id) === NotificationStatusEnum.Unread && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                            >
                              {t("notifications.markAsRead")}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                            className="text-xs sm:text-sm font-medium text-green-600 hover:text-green-700 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t("notifications.view")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="text-center py-12 sm:py-16">
                <Bell className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">
                  {t("notifications.noNotifications")}
                </h3>
                <p className="text-gray-600 text-sm sm:text-base max-w-md mx-auto">
                  {t("notifications.allCaughtUp")}
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {filteredNotifications.length > 0 && (
            <div className="mt-8 sm:mt-12">
              {/* Pagination Info */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                <div className="text-sm text-gray-600">
                  {t("notifications.showingNotifications", {
                    start: ((currentPage - 1) * 10) + 1,
                    end: Math.min(currentPage * 10, userSpecificNotificationsTotalCount || 0),
                    total: userSpecificNotificationsTotalCount || 0
                  })}
                </div>
                
                {/* Page Navigation */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1 || userSpecificNotificationsLoading}
                      className="flex items-center gap-1"
                    >
                      <span>{t("notifications.previous")}</span>
                    </Button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(pageNum)}
                            disabled={userSpecificNotificationsLoading}
                            className="w-8 h-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    {/* Next Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= totalPages || userSpecificNotificationsLoading}
                      className="flex items-center gap-1"
                    >
                      <span>{t("notifications.next")}</span>
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Load More Button (Alternative approach) */}
              {userSpecificNotificationsHasNextPage && (
                <div className="text-center">
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3"
                    onClick={loadMoreNotifications}
                    disabled={userSpecificNotificationsLoading}
                  >
                    {userSpecificNotificationsLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {t("notifications.loadMoreNotifications")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer positioned at bottom */}
      <footer className="mt-auto">
        <SharedFooter3 />
      </footer>
    </div>
  );
};

export default NotificationsPage;